import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Drawer,
  Flex,
  Loader,
  ScrollArea,
  Skeleton,
  Text,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import DocumentNotificationCard from "./DocumentActions";
import { getPendingActions } from "../../../services/dashboard";
import CustomBillingPagination from "../../../components/Pagination/CustomBillingPagination";
import {
  IconClock,
  IconFileAnalytics,
  IconFileAlert,
  IconChevronRight,
  IconShieldCheck,
  IconX,
} from "@tabler/icons-react";

const PendingActionsDrawer = ({
  opened,
  close,
  setSelectedNote,
  setIsOpen,
}) => {
  const { profile } = useSelector((state) => state?.profile);
  const { staff } = useSelector((state) => state.staff);
  const {
    dashboardPendingActions,
    dashboardPendingActionsLoading,
    dashboardPendingActionsCount,
  } = useSelector((state) => state?.dashboard);
  const hasPermissions = profile?.user?.permissions;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (opened) getPendingActions(currentPage);
  }, [currentPage, opened]);

  const items = (Array.isArray(dashboardPendingActions) ? dashboardPendingActions : dashboardPendingActions?.data || [])
    .filter((item) => {
      if (item.type === "document") return true;
      if (hasPermissions && profile?.user?.permissions?.note === false) return false;
      return true;
    });

  return (
    <Drawer
      opened={opened}
      onClose={close}
      title={null}
      withCloseButton={false}
      position="right"
      size="lg"
      styles={{
        header: { display: "none" },
        body: { padding: 0, height: "100vh", display: "flex", flexDirection: "column" },
      }}
      overlayProps={{ opacity: 0.15, blur: 2 }}
      zIndex={2000}
    >
      {/* ── Header ── */}
      <Box style={{ padding: "20px 24px 16px", borderBottom: "1px solid #F3F4F6", flexShrink: 0 }}>
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={10}>
            <Box style={{
              width: 32, height: 32, borderRadius: 8, backgroundColor: "#F3F4F6",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <IconClock size={16} color="#374151" />
            </Box>
            <Text ff="Inter" fw={700} fz={16} color="#1F2937">Pending Actions</Text>
          </Flex>
          <Flex align="center" gap={8}>
            {dashboardPendingActionsCount > 0 && (
              <Text ff="Inter" fz={11} fw={600} c="#9CA3AF">{dashboardPendingActionsCount} item{dashboardPendingActionsCount !== 1 ? "s" : ""}</Text>
            )}
            <ActionIcon size={28} radius={6} variant="subtle"
              styles={{ root: { color: "#9CA3AF", "&:hover": { background: "#F3F4F6", color: "#374151" } } }}
              onClick={close}>
              <IconX size={16} />
            </ActionIcon>
          </Flex>
        </Flex>
      </Box>

      {/* ── Body ── */}
      <Box style={{ flex: 1, overflow: "hidden" }}>
        {dashboardPendingActionsLoading ? (
          <Box style={{ padding: "20px 24px" }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} mb={12} radius={12} height={72} />
            ))}
          </Box>
        ) : items.length > 0 ? (
          <ScrollArea style={{ height: "calc(100vh - 100px)", padding: "20px 24px" }} offsetScrollbars>
            <Text ff="Inter" fz={12} color="#9CA3AF" mb={16}>
              Documents and notes awaiting your review.
            </Text>
            {items.map((item, idx) => {
              if (item?.type === "document") {
                const docName = item?.fileName || item?.name || "Document";
                const entityName = item?.staffName || item?.facilityName || item?.clientName || "";
                return (
                  <Box key={item?._id || idx} mb={8} style={{
                    padding: "12px 0",
                    borderBottom: idx < items.length - 1 ? "1px solid #F3F4F6" : "none",
                    cursor: "pointer", transition: "background 0.1s",
                  }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#FAFAFA"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                  >
                    <Flex align="center" gap={12}>
                      <Box style={{
                        width: 36, height: 36, borderRadius: 10, backgroundColor: "#F3F4F6",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <IconFileAlert size={16} color="#374151" />
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text ff="Inter" fz={13} fw={600} color="#171717" lineClamp={1}>{docName}</Text>
                        <Flex align="center" gap={6} mt={2}>
                          <Badge size="xs" variant="light" color="blue" radius="sm" ff="Inter" fw={500} styles={{ root: { textTransform: "capitalize" } }}>
                            Document
                          </Badge>
                          {entityName && <Text ff="Inter" fz={10} color="#9CA3AF">{entityName}</Text>}
                        </Flex>
                      </Box>
                      <IconChevronRight size={14} color="#D1D5DB" />
                    </Flex>
                  </Box>
                );
              } else if (item?.type === "note") {
                const staffName = staff?.staffs?.find((s) => item?.createdBy === s?.id)?.name || "Staff";
                const noteType = item?.fields?.noteType || item?.noteId?.name || "Note";
                return (
                  <Box key={item?._id || idx} mb={8} style={{
                    padding: "12px 0",
                    borderBottom: idx < items.length - 1 ? "1px solid #F3F4F6" : "none",
                    cursor: "pointer", transition: "background 0.1s",
                  }}
                    onClick={() => { setSelectedNote(item); setIsOpen(true); }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#FAFAFA"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                  >
                    <Flex align="center" gap={12}>
                      <Box style={{
                        width: 36, height: 36, borderRadius: 10, backgroundColor: "#F3F4F6",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <IconFileAnalytics size={16} color="#374151" />
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text ff="Inter" fz={13} fw={600} color="#171717" lineClamp={1}>{staffName}</Text>
                        <Flex align="center" gap={6} mt={2}>
                          <Badge size="xs" variant="light" color="yellow" radius="sm" ff="Inter" fw={500} styles={{ root: { textTransform: "capitalize" } }}>
                            {noteType}
                          </Badge>
                          <Text ff="Inter" fz={10} color="#9CA3AF">{moment(item?.noteDate || item?.createdAt).format("MMM DD, h:mm A")}</Text>
                        </Flex>
                      </Box>
                      <IconChevronRight size={14} color="#D1D5DB" />
                    </Flex>
                  </Box>
                );
              }
              return null;
            })}
            <Box mt={12}>
              <CustomBillingPagination
                data={dashboardPendingActionsCount}
                itemsPerPage={20}
                currentPage={currentPage}
                handlePageChange={(newPage) => setCurrentPage(newPage)}
              />
            </Box>
          </ScrollArea>
        ) : (
          <Flex direction="column" align="center" justify="center" py={60} gap={8}>
            <Box style={{ width: 56, height: 56, borderRadius: "50%", background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <IconShieldCheck size={28} color="#374151" />
            </Box>
            <Text ff="Inter" fz={15} fw={600} color="#374151">All Clear</Text>
            <Text ff="Inter" fz={12} color="#9CA3AF">No pending actions found</Text>
          </Flex>
        )}
      </Box>
    </Drawer>
  );
};

export default PendingActionsDrawer;
